import { NodeWrapper } from "./NodeWrapper";
import { AccessTimeOutlined as AccessTime, InsertDriveFileOutlined as InsertDriveFile, LibraryBooksOutlined as LibraryBooks, MessageOutlined as Message, ImageOutlined as Image, MicNoneOutlined as MicNone, VideocamOutlined as Videocam } from '@mui/icons-material';
import React, { memo, useContext } from "react";
import { Handle } from "react-flow-renderer";
import { useNodeStorage } from "../../../stores/useNodeStorage";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { i18n } from "../../../translate/i18n";
import { mkHeaderStyle, HEADER_ICON_STYLE, HEADER_TEXT_STYLE, NODE_BODY_STYLE, NODE_INFO_ROW, mkInputHandleStyle, mkOutputHandleStyle } from "./nodeStyles";

import { Tooltip, IconButton } from '@material-ui/core';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: '#1e293b',
    color: '#f8fafc',
    maxWidth: 380,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #334155',
    padding: '12px 14px',
    borderRadius: '8px',
    fontFamily: "'Inter', 'Roboto', sans-serif",
    lineHeight: 1.5,
  },
  arrow: { color: '#1e293b' },
}))(Tooltip);

const useStyles = makeStyles((theme) => ({
  pulseIcon: {
    color: '#ffffff',
    borderRadius: '50%',
    animation: '$glowPulse 1.5s infinite',
  },
  '@keyframes glowPulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.9)' },
    '70%': { boxShadow: '0 0 0 8px rgba(255, 255, 255, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(255, 255, 255, 0)' },
  },
}));

export default memo(({ data, isConnectable, id, selected }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const companyId = user?.companyId || localStorage.getItem("companyId") || 1;
  const link = process.env.REACT_APP_BACKEND_URL === "http://localhost:8090" ? "http://localhost:8090" : process.env.REACT_APP_BACKEND_URL;
  const storageItems = useNodeStorage();

  const renderItem = (item, element, index) => {
    const safeValue = element?.value || "";
    const safeOriginal = element?.original || "";
    
    // Helper para urls de media
    const getMediaUrl = (val) => {
      if (!val) return "";
      const filename = val.split(/[/\\]/).pop();
      return `${link}/public/company${companyId}/flow/${filename}`;
    };

    if (item.includes("message")) {
      return (
        <div key={index} style={{ ...NODE_INFO_ROW, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: "4", WebkitBoxOrient: "vertical", marginBottom: "8px" }}>
          {safeValue}
        </div>
      );
    }
    
    if (item.includes("interval")) {
      return (
        <div key={index} style={{ ...NODE_INFO_ROW, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "5px", marginBottom: "8px" }}>
          <AccessTime sx={{ color: "#757575", fontSize: 30 }} />
          <span style={{ fontSize: "12px", color: "#475569" }}>
            {(() => {
              const sec = parseInt(safeValue) || 0;
              if (sec >= 86400 && sec % 86400 === 0) return `${sec / 86400} días`;
              if (sec >= 3600 && sec % 3600 === 0) return `${sec / 3600} horas`;
              if (sec >= 60 && sec % 60 === 0) return `${sec / 60} minutos`;
              return `${sec} ${i18n.t("campaigns.settings.seconds").toLowerCase()}`;
            })()}
          </span>
        </div>
      );
    }
    
    if (item.includes("img")) {
      const mediaUrl = getMediaUrl(safeValue);
      return (
        <div key={index} style={{ ...NODE_INFO_ROW, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", marginBottom: "8px" }}>
          {companyId && safeValue ? (
            <img key={mediaUrl} src={mediaUrl} style={{ width: '100%', maxHeight: '150px', borderRadius: '4px', objectFit: 'contain' }} alt="Flow content" />
          ) : (
            <span>{i18n.t("flowBuilder.editor.nodes.image.noImage")}</span>
          )}
        </div>
      );
    }
    
    if (item.includes("audio")) {
      const mediaUrl = getMediaUrl(safeValue);
      return (
        <div key={index} style={{ ...NODE_INFO_ROW, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", marginBottom: "8px" }}>
          <div style={{ position: "relative", marginBottom: "8px", fontSize: "12px", color: "#3aba38" }}>
            {i18n.t("flowBuilder.editor.nodes.audio.success")}
          </div>
          <audio controls="controls" style={{ width: "100%", maxWidth: "180px" }}>
            <source src={mediaUrl} type="audio/mp3" />
            {i18n.t("flowBuilder.editor.nodes.audio.browserSupport")}
          </audio>
        </div>
      );
    }
    
    if (item.includes("video")) {
      const mediaUrl = getMediaUrl(safeValue);
      return (
        <div key={index} style={{ ...NODE_INFO_ROW, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", marginBottom: "8px" }}>
          <video key={mediaUrl} controls="controls" style={{ width: "100%", maxHeight: "150px", borderRadius: "4px", objectFit: "contain", backgroundColor: "#000" }} src={mediaUrl}>
            {i18n.t("flowBuilder.editor.nodes.video.browserSupport")}
          </video>
        </div>
      );
    }
    
    if (item.includes("document")) {
      return (
        <div key={index} style={{ ...NODE_INFO_ROW, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "5px", marginBottom: "8px" }}>
          {companyId ? (
            <>
              <InsertDriveFile sx={{ fontSize: 40, color: "#757575" }} />
              <span style={{ wordBreak: "break-all", fontSize: "12px", textAlign: "center" }}>{safeValue}</span>
            </>
          ) : (
            <span>Loading...</span>
          )}
        </div>
      );
    }
    
    return null;
  };

  return (
    <NodeWrapper id={id} selected={selected} color="#00E676">
      <Handle type="target" position="left" style={mkInputHandleStyle("#00E676")} onConnect={params => console.log("handle onConnect", params)} isConnectable={isConnectable} />

      <div style={{ ...mkHeaderStyle("#00E676"), justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <LibraryBooks style={HEADER_ICON_STYLE} />
          <div style={HEADER_TEXT_STYLE}>{data?.name || i18n.t("flowBuilder.editor.nodes.content.name")}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <HtmlTooltip
            title={
              <React.Fragment>
                <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: '#38bdf8' }}>
                  💡 {i18n.t("flowBuilder.tooltips.singleBlock.title")}
                </div>
                <div style={{ marginBottom: '0' }}>
                  <div dangerouslySetInnerHTML={{ __html: i18n.t("flowBuilder.tooltips.singleBlock.body") }} />
                </div>
              </React.Fragment>
            }
            placement="right"
          >
            <IconButton size="small" style={{ padding: '4px', marginLeft: '2px' }} className={classes.pulseIcon}>
              <HelpOutlineIcon style={{ fontSize: '15px' }} />
            </IconButton>
          </HtmlTooltip>
        </div>
      </div>
      
      <div style={NODE_BODY_STYLE}>
        {(data?.seq || []).map((item, index) => {
          const element = (data?.elements || []).find(el => el.number === item);
          return renderItem(item, element, index);
        })}
      </div>
      
      <Handle type="source" position="right" id="a" style={{ ...mkOutputHandleStyle("#00E676"), top: "50%", transform: "translateY(-50%)" }} isConnectable={isConnectable} />
    </NodeWrapper>
  );
});
